// Lion Auto: importing pricing groups from Lion Checker
// (LaCheckerImportModal). The server reads the show's filters on Lion
// Checker and proposes one group per filter: create, update (a group an
// earlier import made from the same filter), unchanged or skipped. The pricer
// ticks proposals and imports them; the server recomputes each one and
// refuses any whose signature changed since the preview (409 CHECKER_STALE).
// Each proposal counts our listings its criteria holds today (ownMatches,
// ownSeats; an older server sends neither): each listing counts for its most
// specific filter only (ownOverlap: the ones it fits but loses; specificity:
// the tuple it was ranked by). A create that holds none is skipped
// NO_LISTINGS (fits none) or OUTRANKED (fits some, wins none) unless the
// preview asked for includeEmpty.
// Joined shows: Lion Checker can hold more than one Ticketmaster show with
// this event's Vivid link (the club-seats event, "Club Level Seating: X",
// beside the main show). A newer server joins their filters into one
// preview: each proposal carries checkerEventId, showTag ('' for the
// primary, e.g. 'Club' for a twin) and showOrder, in show then filter order,
// so two proposals can share a filterIndex. A tick is a proposal key
// (proposalKey: show + filter), never a bare filterIndex; an older server
// sends no checkerEventId and its keys are ':<filterIndex>'.
// Moves: importing a filter moves the listings it wins from the less
// specific group that holds them into its group (ranked ones too: they land
// at their Smart sort spot and keep their price, and the old group's ranks
// close up). A listing stays when a group includes it by hand, its group has
// a notes filter or its group is at least as specific. A newer server's
// create or update carries takes, one entry per group it would take
// listings from ({ groupId, name, count, ranked, autoEnabled }; none, or
// missing from an older server, is []), and the import's answer adds moved
// ([{ inventoryId, fromGroupId, toGroupId, ranked }]) and missed (the
// inventory ids whose move did not hold: they stay where they were).
// Pure; no api import.
import { detailLines, retryAfterSec } from './lionAutoErrors';

// Groups one event can hold; mirrors the server's groups.LIMITS.groupsPerEvent
// (backend/services/lionAuto/groups.js): raise the two together.
export const MAX_GROUPS_PER_EVENT = 500;

const SELECTABLE = new Set(['create', 'update']);

const asArray = (x) => (Array.isArray(x) ? x : []);
const asSet = (x) => (x instanceof Set ? x : new Set());
const isSelectable = (p) => !!p && SELECTABLE.has(p.action);
const isGroup = (g) => !!g && typeof g === 'object' && g.id != null;

// A server count (ownMatches, ownSeats): a whole number >= 0, else null
// (missing, as from an older server, is unknown, never 0)
const ownCount = (x) => {
  if ((typeof x !== 'number' && typeof x !== 'string') || String(x).trim() === '') return null;
  const n = Number(x);
  return Number.isInteger(n) && n >= 0 ? n : null;
};
// The proposed criteria holds none of our listings today (unknown is not none)
const holdsNone = (p) => ownCount(p?.ownMatches) === 0;
// A filter the server left out for holding none of our listings: it fits
// none (NO_LISTINGS) or its listings count for more specific filters (OUTRANKED)
const HIDDEN_EMPTY_REASONS = new Set(['NO_LISTINGS', 'OUTRANKED']);
const isHiddenEmpty = (p) => p?.action === 'skipped' && HIDDEN_EMPTY_REASONS.has(p.skipReason);

// A Lion Checker filter_index (0-based) as the 1-based number Lion Checker
// shows; '?' when missing or not an index
const filterNumber = (index) => (Number.isInteger(index) && index >= 0 ? String(index + 1) : '?');
// A joined show's tag ('Club'), trimmed; '' for the primary, a missing tag
// (an older server) or junk
const tagOf = (tag) => (typeof tag === 'string' ? tag.trim() : '');
// "3" for the primary's filter, "Club 3" for a twin's
const filterLabel = (tag, index) => [tagOf(tag), filterNumber(index)].filter(Boolean).join(' ');
const isShow = (s) => !!s && typeof s === 'object';

/**
 * A proposal's key: its show and filter, "<checkerEventId>:<filterIndex>"
 * (the server's key too). An older server sends no checkerEventId:
 * ":<filterIndex>".
 * @param {object} p  a proposal
 * @returns {string}
 */
export function proposalKey(p) {
  return `${p?.checkerEventId ?? ''}:${p?.filterIndex}`;
}

/**
 * The filter's label in the # column and the tick's name: Lion Checker's
 * 1-based number, a joined twin's tag before it ("3", "Club 3"; "?" for a
 * missing index).
 * @param {object} p  a proposal
 */
export function filterLabelText(p) {
  return filterLabel(p?.showTag, p?.filterIndex);
}

// "a, b, c +4 more": the first max labels, then how many are left out
function cutList(labels, max) {
  if (labels.length <= max) return labels.join(', ');
  return `${labels.slice(0, max).join(', ')} +${labels.length - max} more`;
}

/**
 * The import's answer into the cached groups: each updated group replaces
 * the cached one with its id (appended when the cache does not have it yet),
 * each created one is appended; an id appears once (the server's copy wins).
 * Ids compare as strings. Returns the same array when there is nothing to merge.
 * @param {object[]} groups  the cached groups
 * @param {{ created?: object[], updated?: object[] }|null} data  the POST answer
 */
export function mergeImportedGroups(groups, data) {
  const list = asArray(groups);
  const fresh = [...asArray(data?.updated), ...asArray(data?.created)].filter(isGroup);
  if (!fresh.length) return list;
  const byId = new Map();
  for (const g of fresh) {
    if (!byId.has(String(g.id))) byId.set(String(g.id), g);
  }
  const out = list.map(g => byId.get(String(g.id)) || g);
  const seen = new Set(list.map(g => String(g.id)));
  for (const [id, g] of byId) {
    if (!seen.has(id)) out.push(g);
  }
  return out;
}

/**
 * The proposals ticked when the preview opens: every create that holds any
 * of our listings (or an unknown count, from an older server), and every
 * update whose group is off Auto (an Auto group's update is opt-in).
 * @param {object[]} proposals
 * @returns {Set<string>} proposal keys
 */
export function defaultSelection(proposals) {
  return new Set(asArray(proposals)
    .filter(p => (p?.action === 'create' && !holdsNone(p)) || (p?.action === 'update' && !p.autoEnabled))
    .map(proposalKey));
}

/**
 * The rows the preview shows: the filters skipped for holding none of our
 * listings (NO_LISTINGS, OUTRANKED) are left out unless the pricer included them.
 * @param {object[]} proposals
 * @param {boolean} [includeEmpty]
 */
export function visibleProposals(proposals, includeEmpty = false) {
  const list = asArray(proposals);
  return includeEmpty ? list : list.filter(p => !isHiddenEmpty(p));
}

/** How many filters the server left out for holding none of our listings */
export function hiddenEmptyCount(proposals) {
  return asArray(proposals).filter(isHiddenEmpty).length;
}

/**
 * The "include them" checkbox label, or '' when there is nothing to offer
 * (no filter left out and the box not ticked). Left out: the NO_LISTINGS
 * and OUTRANKED count; once included: the creates that hold none of our listings.
 * @param {object[]} proposals
 * @param {boolean} [includeEmpty]
 */
export function includeEmptyText(proposals, includeEmpty = false) {
  const hidden = hiddenEmptyCount(proposals);
  if (!includeEmpty && !hidden) return '';
  const n = includeEmpty
    ? asArray(proposals).filter(p => p?.action === 'create' && holdsNone(p)).length
    : hidden;
  return n === 1
    ? 'Include 1 filter that holds none of our listings'
    : `Include ${n} filters that hold none of our listings`;
}

/**
 * In place of the table when every filter is left out (each holds none of
 * our listings and they are not included), or '' when any row shows.
 * @param {object[]} proposals
 * @param {boolean} [includeEmpty]
 */
export function emptyShownText(proposals, includeEmpty = false) {
  const list = asArray(proposals);
  if (!list.length || visibleProposals(list, includeEmpty).length) return '';
  return list.length === 1
    ? 'The 1 filter holds none of our listings today.'
    : `All ${list.length} filters hold none of our listings today.`;
}

/**
 * The Ours column: how many of our listings the proposed criteria holds
 * today and their seats, "3 · 8 seats"; "0" for none; "?" when the server
 * did not say.
 * @param {object} p  a proposal
 */
export function oursText(p) {
  const listings = ownCount(p?.ownMatches);
  if (listings === null) return '?';
  if (listings === 0) return '0';
  const seats = ownCount(p?.ownSeats);
  if (seats === null) return String(listings);
  return `${listings} · ${seats} seat${seats === 1 ? '' : 's'}`;
}

const isSpecificity = (s) => Array.isArray(s) && s.length === 4
  && s.every(x => typeof x === 'number' && Number.isFinite(x));

/**
 * How specific the server ranked a filter, for the Ours title: "rows · min 2
 * · 3 sections", "any row · any qty · any section" (a min of 1 or less is any
 * qty, as the Qty column says); '' when the server did not send it (older
 * server) or sent junk. The tuple: [rowsNamed 0|1, minQuantity,
 * sectionsNamed 0|1, -sectionCount].
 * @param {object} p  a proposal
 */
export function specificityText(p) {
  const s = p?.specificity;
  if (!isSpecificity(s)) return '';
  const [rowsNamed, minQty, sectionsNamed, negCount] = s;
  const count = -negCount;
  const sections = sectionsNamed > 0 ? `${count} section${count === 1 ? '' : 's'}` : 'any section';
  return [rowsNamed > 0 ? 'rows' : 'any row', minQty > 1 ? `min ${minQty}` : 'any qty', sections].join(' · ');
}

/** A new set with the proposal key ticked or unticked */
export function toggleSelection(set, key) {
  const next = new Set(asSet(set));
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

/** The proposal keys that can be ticked (creates and updates), in the server's order (show, then filter) */
export function selectableKeys(proposals) {
  return asArray(proposals).filter(isSelectable).map(proposalKey);
}

/** Every proposal that can be ticked is, and there is at least one */
export function allSelected(set, proposals) {
  const selectable = selectableKeys(proposals);
  const ticked = asSet(set);
  return selectable.length > 0 && selectable.every(k => ticked.has(k));
}

// The ticked proposals that can be imported, in the server's order
const tickedProposals = (set, proposals) => {
  const ticked = asSet(set);
  return asArray(proposals).filter(p => isSelectable(p) && ticked.has(proposalKey(p)));
};

/**
 * The POST body's selections: the ticked proposals that can be imported,
 * in the server's order (a ticked key the preview no longer offers is
 * dropped). checkerEventId is left out for a proposal without one (an older
 * server); the server reads that as the primary show.
 * @returns {{ checkerEventId?: string, filterIndex: number, sig: string }[]}
 */
export function importSelections(set, proposals) {
  return tickedProposals(set, proposals).map(p => (
    typeof p.checkerEventId === 'string' && p.checkerEventId
      ? { checkerEventId: p.checkerEventId, filterIndex: p.filterIndex, sig: p.sig }
      : { filterIndex: p.filterIndex, sig: p.sig }
  ));
}

/** "101, 102, 103 +4 more"; "any section" for none */
export function sectionsText(sections, max = 6) {
  const list = asArray(sections);
  if (!list.length) return 'any section';
  const labels = list.map(s => s?.name || (s?.sectionId != null ? `#${s.sectionId}` : '?'));
  return cutList(labels, max);
}

/** "A, B, C" (cut after max); "any row" for none */
export function rowsText(rows, max = 8) {
  const list = asArray(rows);
  if (!list.length) return 'any row';
  return cutList(list.map(String), max);
}

const qtyShort = (min) => {
  const n = Number(min);
  return Number.isFinite(n) && n > 1 ? `${n}+` : 'any';
};

/** "Qty 2+"; "any qty" for a minimum of 1 or none */
export function qtyText(min) {
  const short = qtyShort(min);
  return short === 'any' ? 'any qty' : `Qty ${short}`;
}

// How a Lion Checker token found its Skybox section, in the order shown
const METHOD_LABELS = [
  ['id', 'by id'],
  ['name', 'by name'],
  ['fuzzy', 'by fuzzy name'],
  ['token', 'by token'],
  ['token-fuzzy', 'by fuzzy token'],
  ['ignored', 'ignored'],
  ['unresolved', 'unresolved'],
];
const KNOWN_METHODS = new Set(['any', ...METHOD_LABELS.map(([m]) => m)]);

/**
 * How a filter's tokens resolved: "12 by id · 2 by name · 1 unresolved"
 * (non-zero buckets only; an "any section" filter reads "all sections").
 * The live check of the Vivid-ids-are-Skybox-ids assumption.
 */
export function methodText(resolution) {
  const counts = {};
  for (const r of asArray(resolution)) {
    const m = KNOWN_METHODS.has(r?.method) ? r.method : 'other';
    counts[m] = (counts[m] || 0) + 1;
  }
  const parts = counts.any ? ['all sections'] : [];
  for (const [m, label] of [...METHOD_LABELS, ['other', 'other']]) {
    if (counts[m]) parts.push(`${counts[m]} ${label}`);
  }
  return parts.join(' · ');
}

const ACTION_LABELS = {
  create: { text: 'New', kind: 'ok' },
  update: { text: 'Update', kind: 'warn' },
  unchanged: { text: 'Same', kind: 'neutral' },
  skipped: { text: 'Skipped', kind: 'bad' },
};

/** The action chip: { text: 'New'|'Update'|'Same'|'Skipped', kind: 'ok'|'warn'|'neutral'|'bad' } */
export function actionLabel(p) {
  const label = ACTION_LABELS[p?.action];
  if (label) return { ...label };
  return { text: p?.action ? String(p.action) : '?', kind: 'neutral' };
}

const SKIP_TEXT = {
  // a filter naming no section is never imported; its row stays in view (not
  // a hidden empty filter) so the pricer sees why, and the server's warning
  // when an earlier import made an all-sections group from it
  ANY_SECTION: 'Names no section: a group would price against every section',
  NO_SECTIONS: "None of its sections matched this event's Skybox sections",
  NO_LISTINGS: 'None of our listings match this filter',
  OUTRANKED: 'Its listings belong to more specific filters',
  QTY_CONFLICT: "Its min seats is above the group's max Qty",
  GROUP_LIMIT: `No room: an event holds at most ${MAX_GROUPS_PER_EVENT} groups`,
  // selectForImport's answer for a ticked filter that is unchanged
  UNCHANGED: 'Already matches this filter',
};

/** Why a filter is skipped, in words ('' for none) */
export function skipText(reason) {
  if (!reason) return '';
  return SKIP_TEXT[reason] || 'Skipped';
}

// A diff's section count: a number, or the list of sections
const countOf = (x) => {
  if (Array.isArray(x)) return x.length;
  const n = Number(x);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/** What an update changes: "+2 −1 sections · rows changed · qty 2+ → 4+" ('' for nothing) */
export function diffText(diff) {
  if (!diff) return '';
  const parts = [];
  const added = countOf(diff.sections?.added);
  const removed = countOf(diff.sections?.removed);
  if (added || removed) {
    const counts = [added ? `+${added}` : null, removed ? `−${removed}` : null].filter(Boolean).join(' ');
    parts.push(`${counts} section${added + removed === 1 ? '' : 's'}`);
  }
  if (diff.rows) parts.push('rows changed');
  if (diff.minQuantity) parts.push(`qty ${qtyShort(diff.minQuantity.from)} → ${qtyShort(diff.minQuantity.to)}`);
  return parts.join(' · ');
}

/** The show line: "label · venue · date", the date as Lion Checker's venue-local text */
export function showLineText(show) {
  if (!show) return '';
  return [show.label || show.artist, show.venue, show.eventDate].filter(Boolean).join(' · ');
}

/**
 * The preview's joined shows, primary first: a newer server's shows
 * ([{ ...show, tag, showOrder, filterCount }], junk entries left out), else
 * [show] (an older server), else [].
 * @param {object|null} preview  the server's preview
 * @returns {object[]}
 */
export function joinedShows(preview) {
  const shows = asArray(preview?.shows).filter(isShow);
  if (shows.length) return shows;
  return isShow(preview?.show) ? [preview.show] : [];
}

/**
 * One show line per joined show (joinedShows, same order and length), a
 * twin's tag first: "Club · Club Level Seating: X · Ball Arena · 2026-11-03".
 * The primary's line, and an older server's one line, read as showLineText.
 * @param {object|null} preview  the server's preview
 * @returns {string[]}
 */
export function showLinesText(preview) {
  return joinedShows(preview).map(s => [tagOf(s.tag), showLineText(s)].filter(Boolean).join(' · '));
}

const MAX_SHARED_NAMES = 3;
const ownName = (_id, fallback) => fallback;
const NO_LISTINGS_NOTE = 'none of our listings match today';
// The server's own warning on an included empty create ("Filter 3: none
// of our listings match this filter today", or "Filter 3: its listings
// belong to more specific filters" for an outranked one): when a proposal
// carries it, NO_LISTINGS_NOTE is left out so the row says it once
const SAYS_NO_LISTINGS = /none of our listings match|belong to more specific filters/i;

// "+2 more fit this filter but belong to more specific ones" (ownOverlap:
// listings it fits that count for a more specific filter), '' for none or unknown
function overlapText(overlap) {
  const n = ownCount(overlap);
  if (!n) return '';
  return n === 1
    ? '+1 more fits this filter but belongs to more specific ones'
    : `+${n} more fit this filter but belong to more specific ones`;
}

// The takes line's title (hover)
const TAKES_TITLE = 'Listings a less specific group holds today that move to this group on import';

/**
 * The note column's lines: why it is skipped, what an update changes, the
 * listings a create or update takes from less specific groups (takesText,
 * with its own title), that a create or update holds none of our listings
 * today, how many more it fits that count for more specific filters (any
 * action), the groups it shares sections with (their current name via
 * nameOf), the Lion Checker fields Lion Auto ignores, then the server's
 * warnings. The no-listings line is left out when a server warning says it
 * already, or when the "+N more" line explains the 0 (it fits listings that
 * count for other filters).
 * @param {object} p  a proposal
 * @param {(groupId, fallbackName) => string} [nameOf]
 * @returns {{ text: string, warn: boolean, title?: string }[]}
 */
export function noteLines(p, nameOf = ownName) {
  if (!p) return [];
  const lines = [];
  if (p.action === 'skipped') lines.push({ text: skipText(p.skipReason), warn: true });
  const diff = p.action === 'update' ? diffText(p.diff) : '';
  if (diff) lines.push({ text: diff, warn: false });
  const takes = takesText(p, nameOf);
  if (takes) lines.push({ text: takes, warn: false, title: TAKES_TITLE });
  const warnings = asArray(p.warnings).map(String);
  const empty = (p.action === 'create' || p.action === 'update') && holdsNone(p);
  const overlap = overlapText(p.ownOverlap);
  const saidAlready = !!overlap || warnings.some(w => SAYS_NO_LISTINGS.test(w));
  if (empty && !saidAlready) lines.push({ text: NO_LISTINGS_NOTE, warn: true });
  if (overlap) lines.push({ text: overlap, warn: false });
  const shared = asArray(p.sharedWith).map(s => nameOf(s?.groupId, s?.groupName) || `#${s?.groupId}`);
  if (shared.length) lines.push({ text: `shares sections with ${cutList(shared, MAX_SHARED_NAMES)}`, warn: false });
  if (p.ignored?.priceBand) lines.push({ text: 'price band ignored', warn: false });
  if (p.ignored?.exclTypes) lines.push({ text: 'ticket types ignored', warn: false });
  for (const w of warnings) lines.push({ text: w, warn: true });
  return lines;
}

/**
 * A group whose Lion Checker filter is gone (kept, never deleted):
 * "Checker 6 group · filter 6 · on Auto", "… · filter Club 2" for a joined
 * twin's. The server's orphan is { groupId, name, checkerEventId,
 * checkerFilterIndex (0-based), autoEnabled, showTag ('' for the primary or
 * a show not joined; an older server sends none) }.
 * @param {object} o  an orphan
 * @param {(groupId, fallbackName) => string} [nameOf]  the group's current name
 */
export function orphanText(o, nameOf = ownName) {
  if (!o) return '';
  const parts = [nameOf(o.groupId, o.name) || `#${o.groupId}`, `filter ${filterLabel(o.showTag, o.checkerFilterIndex)}`];
  if (o.autoEnabled) parts.push('on Auto');
  return parts.join(' · ');
}

// The groups one row's takes names before the rest are summed, and the
// filters the confirm lists before "+N more filters"
const MAX_TAKES_GROUPS = 3;
const MAX_MOVE_LINES = 8;
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
const rankedSuffix = (n) => (n ? ` (${n} ranked)` : '');
const takeGroupName = (t, nameOf) => nameOf(t.groupId, t.name) || `#${t.groupId}`;

// A create's or update's takes, junk left out: a group id, a whole count of
// 1 or more, ranked a whole number no higher than the count (junk is 0),
// autoEnabled read as on/off (the server's 1/0 too). [] for any other action.
function takesOf(p) {
  if (!isSelectable(p)) return [];
  return asArray(p.takes).flatMap((t) => {
    const count = ownCount(t?.count);
    if (!count || t.groupId == null) return [];
    const ranked = Math.min(ownCount(t.ranked) || 0, count);
    return [{ groupId: t.groupId, name: t.name, count, ranked, autoEnabled: !!t.autoEnabled }];
  });
}

// "a", "a and b", "a, b and c"
function joinAnd(parts) {
  if (parts.length <= 1) return parts.join('');
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

// The group a proposal makes or updates: an update's current name (nameOf),
// else the proposal's name, else "Filter 3"
const targetName = (p, nameOf) => (p.groupId != null && nameOf(p.groupId, p.name))
  || p.name || `Filter ${filterLabelText(p)}`;
// Its group is off Auto after the import: a create always starts off Auto
const targetOffAuto = (p) => p.action === 'create' || !p.autoEnabled;

/**
 * The listings a create or update would take from other, less specific
 * groups: "takes 4 from Checker 1 · 101-110 (3 ranked)", several groups
 * joined ("takes 4 from A (3 ranked), 2 from B and 1 from C"), past three
 * the rest summed ("… and 3 more from 2 other groups (1 ranked)"). '' when
 * it takes none, for any other action, or from an older server (no takes).
 * @param {object} p  a proposal
 * @param {(groupId, fallbackName) => string} [nameOf]  each group's current name
 */
export function takesText(p, nameOf = ownName) {
  const takes = takesOf(p);
  if (!takes.length) return '';
  const parts = takes.slice(0, MAX_TAKES_GROUPS)
    .map(t => `${t.count} from ${takeGroupName(t, nameOf)}${rankedSuffix(t.ranked)}`);
  const rest = takes.slice(MAX_TAKES_GROUPS);
  if (rest.length) {
    const count = rest.reduce((n, t) => n + t.count, 0);
    const ranked = rest.reduce((n, t) => n + t.ranked, 0);
    parts.push(`${count} more from ${plural(rest.length, 'other group')}${rankedSuffix(ranked)}`);
  }
  return `takes ${joinAnd(parts)}`;
}

// "Import 2 groups?", then the ticked updates on Auto (their criteria change)
function importAskText(n, onAuto) {
  const ask = n === 1 ? 'Import 1 group?' : `Import ${n} groups?`;
  if (!onAuto) return ask;
  const change = "sections, rows or Qty change on the runner's next pass.";
  if (n === 1) return `${ask} It is on Auto: its ${change}`;
  const which = onAuto === 1 ? '1 of them is on Auto: its' : `${onAuto} of them are on Auto: their`;
  return `${ask} ${which} ${change}`;
}

// One line per ticked filter that takes listings, by the group they move to
// ("Checker 3 · 224 takes …"), cut after MAX_MOVE_LINES
function movesText(moving, nameOf) {
  const lines = moving.slice(0, MAX_MOVE_LINES).map(p => `${targetName(p, nameOf)} ${takesText(p, nameOf)}`);
  const rest = moving.length - MAX_MOVE_LINES;
  if (rest > 0) lines.push(`+${plural(rest, 'more filter')}`);
  return ['Listings move from less specific groups:', ...lines].join('\n');
}

// The groups on Auto that lose ranked listings: once each (ids compare as
// strings), their ranked summed over the ticked filters, in first-seen order
function autoLosses(moving) {
  const byId = new Map();
  for (const t of moving.flatMap(p => takesOf(p))) {
    if (!t.autoEnabled || !t.ranked) continue;
    const id = String(t.groupId);
    const prev = byId.get(id);
    byId.set(id, prev ? { ...prev, ranked: prev.ranked + t.ranked } : t);
  }
  return [...byId.values()];
}

// The warning when ranked listings leave a group on Auto ('' for none): its
// runner reprices the closed-up ranks on its own
function autoLossText(losses, nameOf) {
  if (!losses.length) return '';
  if (losses.length === 1) {
    const [g] = losses;
    return `${takeGroupName(g, nameOf)} is on Auto and loses ${plural(g.ranked, 'ranked listing')}: `
      + 'its ranks close up and reprice on its next run.';
  }
  const names = cutList(losses.map(g => takeGroupName(g, nameOf)), MAX_SHARED_NAMES);
  return `${losses.length} groups on Auto lose ranked listings (${names}): `
    + 'their ranks close up and reprice on their next run.';
}

// "a, b and c"; past three, "a, b, c and 2 more groups"
function groupNamesText(names) {
  if (names.length <= MAX_SHARED_NAMES) return joinAnd(names);
  const rest = names.length - MAX_SHARED_NAMES;
  return `${names.slice(0, MAX_SHARED_NAMES).join(', ')} and ${plural(rest, 'more group')}`;
}

// The ranked listings of groups on Auto that move into a group off Auto (a
// create, or an update off Auto): they leave Auto, and nothing reprices them
// until a pricer applies that ladder or turns its Auto on ('' for none)
function leaveAutoText(moving, nameOf) {
  const targets = moving.filter(targetOffAuto).map(p => ({
    name: targetName(p, nameOf),
    ranked: takesOf(p).reduce((n, t) => n + (t.autoEnabled ? t.ranked : 0), 0),
  })).filter(x => x.ranked > 0);
  if (!targets.length) return '';
  const ranked = targets.reduce((n, x) => n + x.ranked, 0);
  const names = groupNamesText(targets.map(x => x.name));
  if (targets.length > 1) {
    return `${ranked} ranked listings leave Auto: they move to ${names}, which are off Auto, `
      + 'and keep their current price until you apply their ladders or turn Auto on.';
  }
  if (ranked === 1) {
    return `1 ranked listing leaves Auto: it moves to ${names}, which is off Auto, `
      + 'and keeps its current price until you apply its ladder or turn Auto on.';
  }
  return `${ranked} ranked listings leave Auto: they move to ${names}, which is off Auto, `
    + 'and keep their current price until you apply its ladder or turn Auto on.';
}

/**
 * The confirm before an import that touches Auto, or null when it does not.
 * It asks when a ticked update is on Auto, or a ticked filter takes ranked
 * listings from a group on Auto (they may also leave Auto); moves that touch
 * no Auto group show in the row notes only, so the confirm is not clicked
 * through by habit. The text: the ask (and the ticked updates on Auto), a
 * line per ticked filter that takes listings, by its group's name
 * (takesText), a warning per kind: a group on Auto closes up its ranks, and
 * ranked listings leave Auto for a group off Auto. Paragraphs are split by a
 * blank line (window.confirm text).
 * @param {Set<string>} set  the ticked proposal keys
 * @param {object[]} proposals
 * @param {(groupId, fallbackName) => string} [nameOf]  each group's current name
 * @returns {string|null}
 */
export function confirmImportText(set, proposals, nameOf = ownName) {
  const chosen = tickedProposals(set, proposals);
  const onAuto = chosen.filter(p => p.action === 'update' && p.autoEnabled).length;
  const moving = chosen.filter(p => takesOf(p).length > 0);
  const losses = autoLosses(moving);
  if (!onAuto && !losses.length) return null;
  const ask = importAskText(chosen.length, onAuto);
  if (!moving.length) return ask;
  return [ask, movesText(moving, nameOf), autoLossText(losses, nameOf), leaveAutoText(moving, nameOf)]
    .filter(Boolean).join('\n\n');
}

function showNotFoundText(data) {
  if (data.reason === 'NO_VIVID_ID') {
    return "This event has no VividSeats link in Skybox, so it can't be matched on Lion Checker.";
  }
  if (data.reason === 'NOT_ON_WATCHLIST') {
    const production = data.vividProductionId
      ? `VividSeats production ${data.vividProductionId}`
      : "this event's VividSeats production";
    return `Lion Checker has no show with ${production}. Add its VividSeats link on Lion Checker (/links).`;
  }
  return 'Lion Checker has no show for this event.';
}

function rejectedText(data) {
  const lines = detailLines(data.details);
  if (data.code === 'AUTO_NEEDS_FLOOR') {
    const head = 'A group on Auto needs a floor (group, show or artist)';
    return lines.length ? `${head}: ${lines.join('; ')}` : `${head}. Untick it or set a floor first.`;
  }
  if (lines.length) return `The import was not accepted: ${lines.join('; ')}`;
  return data.error ? `The import was not accepted: ${data.error}` : 'The import was not accepted.';
}

const BY_CODE = {
  CHECKER_DISABLED: 'Lion Checker import is not set up on this server (RESTOCK_DATABASE_URL).',
  CHECKER_UNAVAILABLE: "Lion Checker's database did not answer. Try again in a minute.",
  CHECKER_STALE: "Lion Checker's filters changed since this preview. Reload the preview and check the list again.",
  PRICING_NOT_FOUND: "Skybox has no pricing for this event, so its sections can't be matched.",
};
const FALLBACK = 'The Lion Checker import failed.';
const MISCONFIGURED = 'Lion Checker import is misconfigured on this server (database login, role or schema). Tell an admin.';

// A 502 CHECKER_UNAVAILABLE with retryable: false: Lion Checker's database
// answered but refused (bad login, a missing role grant, a renamed table), so
// a retry fails the same way. An older server sends no flag: retryable.
const isMisconfigured = (data) => data?.code === 'CHECKER_UNAVAILABLE' && data.retryable === false;

/**
 * A failed preview (read: true) or import in words, per the server's code.
 * An import with no answer may have gone through: the text says to reload.
 * @param {object|null} err  the axios error
 * @param {{ read?: boolean }} [opts]
 */
export function checkerErrorText(err, { read = false } = {}) {
  if (!err) return FALLBACK;
  const status = err.response?.status;
  const raw = err.response?.data;
  const data = raw && typeof raw === 'object' ? raw : {};
  if (!status) {
    return read ? 'The server did not answer. Try again.'
      : 'The server did not answer: reload the preview to see whether anything was imported.';
  }
  if (isMisconfigured(data)) return MISCONFIGURED;
  if (BY_CODE[data.code]) return BY_CODE[data.code];
  if (data.code === 'CHECKER_SHOW_NOT_FOUND') return showNotFoundText(data);
  if (status === 429) return `Too many requests. Try again in ${retryAfterSec(err)} s.`;
  if (status === 400) return rejectedText(data);
  if (status === 403) return 'You do not have access to this event.';
  // A bare { error } with no code is the server's unknown-route answer
  if (status === 404 && !data.code) return 'Lion Checker import is not available on this server yet.';
  if (status === 503 && data.retryable) return 'Skybox did not answer. Try again in a minute.';
  return data.error || FALLBACK;
}

/** A failed preview worth a Retry button (not: not set up, misconfigured, no show, no access) */
export function checkerPreviewRetryable(err) {
  const status = err?.response?.status;
  const code = err?.response?.data?.code;
  if (code === 'CHECKER_DISABLED' || code === 'CHECKER_SHOW_NOT_FOUND') return false;
  if (isMisconfigured(err?.response?.data)) return false;
  return status !== 403;
}

/** A failed import that calls for "Reload preview": stale (409) or no answer (outcome unknown) */
export function importNeedsReload(err) {
  if (!err) return false;
  const status = err.response?.status;
  return !status || status === 409;
}

// The import's error statuses that leave the cached groups and matches stale:
// 404 (a group gone), 409 CHECKER_STALE, and the two whose outcome is
// unknown, a 500 and no answer at all (undefined: a timeout or a dropped
// connection), either of which can come after the server's batch committed:
// re-read the groups and matches (useImportLionAutoChecker)
export const REFRESH_ON_IMPORT = Object.freeze([404, 409, 500, undefined]);

/**
 * A failed group write whose groups and matches must be re-read: its status
 * (undefined when the server did not answer) is in refreshOn.
 * @param {(number|undefined)[]} refreshOn
 * @param {object|null} err  the axios error
 */
export function shouldRefreshOn(refreshOn, err) {
  if (!err) return false;
  return asArray(refreshOn).includes(err.response?.status);
}

/**
 * The import's answer in words. Anything created or updated: "Imported:
 * Created 2 · Updated 1 · Skipped 1 · Moved 4 listings · 1 listing stayed
 * in its group" (non-zero counts only; moved and missed are missing from an
 * older server). Nothing (the server's 200 when every ticked filter was
 * unchanged or skipped): "Nothing was imported · filter 3: Already matches
 * this filter", one reason per filter ("filter Club 3: …" for a joined
 * twin's: a skipped entry's showTag).
 */
export function importResultText(data) {
  const created = asArray(data?.created).length;
  const updated = asArray(data?.updated).length;
  const skipped = asArray(data?.skipped);
  if (!created && !updated) {
    const why = skipped.map(s => `filter ${filterLabel(s?.showTag, s?.filterIndex)}: ${skipText(s?.reason) || 'Skipped'}`);
    return ['Nothing was imported', ...why].join(' · ');
  }
  const parts = [['Created', created], ['Updated', updated], ['Skipped', skipped.length]]
    .filter(([, n]) => n > 0).map(([label, n]) => `${label} ${n}`);
  const moved = asArray(data?.moved).length;
  const missed = asArray(data?.missed).length;
  if (moved) parts.push(`Moved ${plural(moved, 'listing')}`);
  if (missed) parts.push(`${plural(missed, 'listing')} stayed in ${missed === 1 ? 'its group' : 'their groups'}`);
  return `Imported: ${parts.join(' · ')}`;
}
